import {
	Button,
	Col,
	Flex,
	Input,
	Row,
	Segmented,
	Splitter,
	TableProps,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import Header from '../../../components/Header';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { Material } from '../../../interfaces/MaterialInventory.interface.ts';
import {
	useGetMaterialCategoriesQuery,
	useGetMaterialShapesQuery,
	useGetMaterialsQuery,
	useGetMaterialShelvesQuery,
	useGetMaterialTypesQuery,
	useGetMaterialVendorsQuery,
	useGetMaterialDimensionRangesQuery,
	useGetInventoryLocationsQuery,
} from '../../../redux/api/material.ts';
import MaterialModal from '../../../components/MaterialModal/index.tsx';
import { useDispatch } from 'react-redux';
import { showAddMaterialModal } from '../../../redux/slices/material.ts';
import { MaterialTableFilter } from '../../../components/MaterialTableFilter/index.tsx';
import MaterialInventoryTable from '../../../components/MaterialInventoryTable/index.tsx';
import { SorterResult } from 'antd/es/table/interface';
import { RECTANGLE_BAR } from '../../../config/constants.ts';
import { getEffectiveShapeName } from '../../../utils/shapeUtils.ts';

interface IQueryParams {
	search?: string;
	page?: number;
	ordering?: string;
	availability_status?: string;
	vendors?: string;
	shape?: string;
	category?: string;
	types?: string;
	min_total_available_length?: string;
	has_certificate?: string;
	outer_diameter__gte?: string;
	outer_diameter__lte?: string;
	inner_diameter__gte?: string;
	inner_diameter__lte?: string;
	width__gte?: string;
	width__lte?: string;
	height__gte?: string;
	height__lte?: string;
	wall_thickness__gte?: string;
	wall_thickness__lte?: string;
	group_by_length?: string;
	location_id?: string;
	bundle_friendly_id?: string;
	total_length__gte?: string;
	total_length__lte?: string;
	material_lengths_count__gte?: string;
	material_lengths_count__lte?: string;
	building?: string;
}

function MaterialInventory() {
	const location = useLocation();
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const [searchValue, setSearchValue] = useState<string>('');

	const queryParams = new URLSearchParams(location.search);
	const search = queryParams.get('search') || '';
	const currentPageUrl = Number(queryParams.get('page')) || 1;
	const ordering = queryParams.get('ordering') || '';
	const vendors = queryParams.get('vendors') || '';
	const shape = queryParams.get('shape') || '';
	const category = queryParams.get('category') || '';
	const types = queryParams.get('types') || '';
	const min_total_available_length =
		queryParams.get('min_total_available_length') || '';
	const has_certificate = queryParams.get('has_certificate') || '';
	const outer_diameter__gte = queryParams.get('outer_diameter__gte') || '';
	const outer_diameter__lte = queryParams.get('outer_diameter__lte') || '';
	const inner_diameter__gte = queryParams.get('inner_diameter__gte') || '';
	const inner_diameter__lte = queryParams.get('inner_diameter__lte') || '';
	const width__gte = queryParams.get('width__gte') || '';
	const width__lte = queryParams.get('width__lte') || '';
	const height__gte = queryParams.get('height__gte') || '';
	const height__lte = queryParams.get('height__lte') || '';
	const wall_thickness__gte = queryParams.get('wall_thickness__gte') || '';
	const wall_thickness__lte = queryParams.get('wall_thickness__lte') || '';
	const group_by_length = queryParams.get('group_by_length') || '';
	const location_id = queryParams.get('location_id') || '';
	const bundle_friendly_id = queryParams.get('bundle_friendly_id') || '';
	const total_length__gte = queryParams.get('total_length__gte') || '';
	const total_length__lte = queryParams.get('total_length__lte') || '';
	const material_lengths_count__gte =
		queryParams.get('material_lengths_count__gte') || '';
	const material_lengths_count__lte =
		queryParams.get('material_lengths_count__lte') || '';
	const building = queryParams.get('building') || '';

	const materialQuery: IQueryParams = {};

	if (search) {
		materialQuery.search = search;
	}

	if (currentPageUrl) {
		materialQuery.page = currentPageUrl;
	}

	if (ordering && ordering !== '') {
		materialQuery.ordering = ordering;
	}

	if (vendors && vendors !== '') {
		materialQuery.vendors = vendors;
	}

	if (shape && shape !== '') {
		materialQuery.shape = shape;
	}

	if (category && category !== '') {
		materialQuery.category = category;
	}

	if (types && types !== '') {
		materialQuery.types = types;
	}

	if (min_total_available_length !== '') {
		materialQuery.min_total_available_length = min_total_available_length;
	}

	if (has_certificate !== '') {
		materialQuery.has_certificate = has_certificate;
	}

	if (outer_diameter__gte && outer_diameter__gte !== '') {
		materialQuery.outer_diameter__gte = outer_diameter__gte;
	}

	if (outer_diameter__lte && outer_diameter__lte !== '') {
		materialQuery.outer_diameter__lte = outer_diameter__lte;
	}

	if (inner_diameter__gte && inner_diameter__gte !== '') {
		materialQuery.inner_diameter__gte = inner_diameter__gte;
	}

	if (inner_diameter__lte && inner_diameter__lte !== '') {
		materialQuery.inner_diameter__lte = inner_diameter__lte;
	}

	if (width__gte && width__gte !== '') {
		materialQuery.width__gte = width__gte;
	}

	if (width__lte && width__lte !== '') {
		materialQuery.width__lte = width__lte;
	}

	if (height__gte && height__gte !== '') {
		materialQuery.height__gte = height__gte;
	}

	if (height__lte && height__lte !== '') {
		materialQuery.height__lte = height__lte;
	}

	if (wall_thickness__gte && wall_thickness__gte !== '') {
		materialQuery.wall_thickness__gte = wall_thickness__gte;
	}

	if (wall_thickness__lte && wall_thickness__lte !== '') {
		materialQuery.wall_thickness__lte = wall_thickness__lte;
	}

	if (location_id && location_id !== '') {
		materialQuery.location_id = location_id;
	}

	if (bundle_friendly_id && bundle_friendly_id !== '') {
		materialQuery.bundle_friendly_id = bundle_friendly_id;
	}

	if (total_length__gte && total_length__gte !== '') {
		materialQuery.total_length__gte = total_length__gte;
	}

	if (total_length__lte && total_length__lte !== '') {
		materialQuery.total_length__lte = total_length__lte;
	}

	if (material_lengths_count__gte && material_lengths_count__gte !== '') {
		materialQuery.material_lengths_count__gte = material_lengths_count__gte;
	}

	if (material_lengths_count__lte && material_lengths_count__lte !== '') {
		materialQuery.material_lengths_count__lte = material_lengths_count__lte;
	}

	if (building && building !== '') {
		materialQuery.building = building;
	}

	const { data, isLoading, isFetching, refetch } = useGetMaterialsQuery(
		materialQuery,
		{
			refetchOnFocus: true,
			refetchOnMountOrArgChange: true,
		}
	);

	const refetchData = async () => {
		try {
			await refetch();
		} catch (error) {
			console.error('Error refetching materials:', error);
		}
	};

	const shapesQuery = useGetMaterialShapesQuery();
	const shelvesQuery = useGetMaterialShelvesQuery();

	const vendorsQuery = useGetMaterialVendorsQuery();
	const categoriesQuery = useGetMaterialCategoriesQuery();
	const typesQuery = useGetMaterialTypesQuery(
		{ material_category: category },
		{
			skip: !categoriesQuery.isSuccess || !category || category === '',
			refetchOnMountOrArgChange: true,
		}
	);

	const [materials, setMaterials] = useState<Material[]>([]);
	const [currentPage, setCurrentPage] = useState<number>(currentPageUrl);
	const [totalRecords, setTotalRecords] = useState<number>(0);

	const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(
		null
	);

	const rangeQueryParams = {
		shape: shape || undefined,
		category: category || undefined,
		types: types || undefined,
		vendors: vendors || undefined,
		location_id: location_id || undefined,
		bundle_friendly_id: bundle_friendly_id || undefined,
		has_certificate: has_certificate || undefined,
	};

	const { data: dimensionRanges } = useGetMaterialDimensionRangesQuery(
		rangeQueryParams,
		{
			skip: !shape,
		}
	);

	const [searchTextLocation, setSearchTextLocation] = useState('');
	const { data: locationsData, isFetching: isFetchingLocations } =
		useGetInventoryLocationsQuery(
			searchTextLocation ? { search: searchTextLocation } : {}
		);
	const debounceRef = useRef<number | null>(null);
	const handleLocationSearch = (value: string) => {
		if (debounceRef.current) {
			clearTimeout(debounceRef.current);
		}

		debounceRef.current = window.setTimeout(() => {
			if (value.trim().length >= 2) {
				setSearchTextLocation(value);
			}
		}, 400);
	};

	const handleTableChange: TableProps<Material>['onChange'] = (
		_pagination,
		_filters,
		sorter,
		extra
	) => {
		if (extra.action === 'sort') {
			const orderType = (sorter as SorterResult<Material>)?.order;
			const columnKey = (sorter as SorterResult<Material>)?.columnKey;
			const sorterColumn = (sorter as SorterResult<Material>)?.column;

			const column =
				orderType === 'ascend' || orderType === undefined
					? columnKey
					: '-' + columnKey;
			if (column !== undefined && sorterColumn !== undefined) {
				queryParams.set('ordering', column?.toString());
			} else {
				queryParams.delete('ordering');
			}
			navigate({ search: queryParams.toString() });
		}
	};

	const pageChanged = async (page: number) => {
		queryParams.set('page', page.toString());
		navigate({ search: queryParams.toString() });
		setCurrentPage(page);
	};

	const getShapeNameById = (id: string) => {
		if (id && shapesQuery.data) {
			return getEffectiveShapeName(shapesQuery.data.find((x) => x.id === id)?.display_name);
		}
	};

	useEffect(() => {
		if (search) {
			setSearchValue(search);
		}
	}, [search]);

	useEffect(() => {
		if (data) {
			setMaterials(data.results as Material[]);
			setTotalRecords(data.count ? data.count : 0);

			// If there is only a single result returned from the query,
			// then set the shape to match the material from the query
			// BUT only if there's no shape parameter already in the URL
			// (to avoid overwriting explicitly set shape parameters)
			const currentQueryParams = new URLSearchParams(location.search);
			const currentShape = currentQueryParams.get('shape');
			if (search && search !== '' && data.count === 1 && !currentShape) {
				currentQueryParams.set('shape', String(data.results[0].shape.id));
				navigate({ search: currentQueryParams.toString() }, { replace: true });
			}
		}
	}, [data, isFetching, search, navigate, location.search]);

	const getValue = (field: string, limit: number, dataField: string) => {
		if (limit) {
			return Number(limit);
		}
		if (data) {
			const results: any = data.results;
			const sorted = results
				.map((x: any) => x[field])
				.sort((a: any, b: any) => (dataField === 'gte' ? a - b : b - a));
			return Number(sorted[0]);
		}
	};

	return (
		<main
			data-testid="page-fastlane-material-inventory"
			data-ready={!isLoading && !isFetching && data ? 'true' : 'false'}
		>
			<Flex justify="space-between" align="center" className="mb-4">
				<Header
					title="Material Inventory"
					breadcrumbItems={[
						{
							title: <Link to="/">Home</Link>,
						},
						{
							title: 'Material Inventory',
						},
					]}
				/>
				<div className="flex flex-col gap-2 items-end">
					<Segmented
						options={[
							{ label: 'Material', value: 'material' },
							{ label: 'Lengths', value: 'lengths' },
						]}
						value="material"
						onChange={(value) => {
							if (value === 'lengths') {
								navigate('/material/lengths');
							}
						}}
					/>
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={() => dispatch(showAddMaterialModal())}
					>
						Add Material
					</Button>
				</div>
			</Flex>

			<Row>
				<Col span={24}>
					<Splitter style={{ width: '100%', columnGap: '2px' }}>
						<Splitter.Panel defaultSize={250} collapsible={true}>
							<Input.Search
								placeholder="Search"
								className="mb-2 w-full"
								allowClear={true}
								value={searchValue}
								onClear={() => {
									setSearchValue('');
									queryParams.delete('search');
									navigate({ search: queryParams.toString() });
								}}
								onChange={(e) => {
									setSearchValue(e.target.value);
								}}
								onPressEnter={(e) => {
									setSearchValue(e.target.value);
									queryParams.set('search', String(e.target.value));
									navigate({ search: queryParams.toString() });
								}}
							/>
							<MaterialTableFilter
								search={search}
								vendors={vendorsQuery.data ? vendorsQuery.data : []}
								vendors_selected={vendors}
								shapes={shapesQuery.data ? shapesQuery.data : []}
								shape_selected={shape}
								categories={categoriesQuery.data ? categoriesQuery.data : []}
								category_selected={category}
								types={typesQuery.data ? typesQuery.data : []}
								types_selected={types}
								min_total_available_length={min_total_available_length}
								has_certificate={has_certificate}
								outer_diameter_gte={getValue(
									'outer_diameter',
									Number(outer_diameter__gte),
									'gte'
								)}
								outer_diameter_lte={getValue(
									'outer_diameter',
									Number(outer_diameter__lte),
									'lte'
								)}
								inner_diameter_gte={getValue(
									'inner_diameter',
									Number(inner_diameter__gte),
									'gte'
								)}
								inner_diameter_lte={getValue(
									'inner_diameter',
									Number(inner_diameter__lte),
									'lte'
								)}
								width_gte={getValue('width', Number(width__gte), 'gte')}
								width_lte={getValue('width', Number(width__lte), 'lte')}
								height_gte={getValue('height', Number(height__gte), 'gte')}
								height_lte={getValue('height', Number(height__lte), 'lte')}
								wall_thickness_gte={getValue(
									'wall_thickness',
									Number(wall_thickness__gte),
									'gte'
								)}
								wall_thickness_lte={getValue(
									'wall_thickness',
									Number(wall_thickness__lte),
									'lte'
								)}
								material_length_id=""
								locations={locationsData ? locationsData.results : []}
								location_id_selected={location_id}
								loading={isFetchingLocations}
								onSearchLocation={handleLocationSearch}
								resetFiltersSignal={() => setSearchTextLocation('')}
								bundle_friendly_id={bundle_friendly_id}
								heightRange={dimensionRanges?.height || { min: 0, max: 999 }}
								widthRange={dimensionRanges?.width || { min: 0, max: 999 }}
								totalAvailableLengthRange={
									dimensionRanges?.total_available_length || {
										min: 0,
										max: 1000,
									}
								}
								outerDiameterRange={dimensionRanges?.outer_diameter}
								innerDiameterRange={dimensionRanges?.inner_diameter}
								wallThicknessRange={dimensionRanges?.wall_thickness}
							/>
						</Splitter.Panel>
						<Splitter.Panel>
							<MaterialInventoryTable
								key={`material-inventory-table`} // Force re-render when data changes
								data={materials}
								tableProps={{
									size: 'large',
									expandable: true,
								}}
								onChange={handleTableChange}
								showActionsColumn={true}
								recordsTotal={totalRecords}
								currentPage={currentPage}
								onChangePage={pageChanged}
								isLoading={isLoading || isFetching}
								selectedShape={
									getShapeNameById(shape)
										? getShapeNameById(shape)
										: RECTANGLE_BAR
								}
								showDetailsPane={true}
								showMaterialCertificatesPane={true}
								showTotalLengthColumn={true}
								showReturnRemnantButton={true}
								setSelectedMaterial={setSelectedMaterial}
								refetchData={refetchData}
								groupByLength={group_by_length}
								queryParams={queryParams}
								navigate={navigate}
								types={typesQuery.data ? typesQuery.data : []}
								// shelves={shelvesQuery.data ? shelvesQuery.data : []}
								locations={locationsData ? locationsData.results : []}
								onSearchLocation={handleLocationSearch}
								locationsLoading={isFetchingLocations}
								categories={categoriesQuery.data ? categoriesQuery.data : []}
								heightRange={dimensionRanges?.height}
								widthRange={dimensionRanges?.width}
								outerDiameterRange={dimensionRanges?.outer_diameter}
								innerDiameterRange={dimensionRanges?.inner_diameter}
								wallThicknessRange={dimensionRanges?.wall_thickness}
								totalLengthRange={dimensionRanges?.total_length}
								totalAvailableLengthRange={
									dimensionRanges?.total_available_length
								}
								materialLengthsCountRange={
									dimensionRanges?.material_lengths_count
								}
							/>
						</Splitter.Panel>
					</Splitter>
				</Col>
			</Row>

			<MaterialModal
				material={selectedMaterial}
				refetchData={refetchData}
				onClose={() => setSelectedMaterial(null)}
			/>
		</main>
	);
}

export default MaterialInventory;
